import { useEffect, useRef, useState } from "react";

import { SystemArchitectureCatalogue } from "./SystemArchitectureCatalogue";
import {
  getSystemArchitectureDiagram,
  type SystemArchitectureDiagramId,
} from "./systemArchitectureRegistry";
import {
  ARCHITECTURE_DIAGRAM_RENDER_ERROR_CODE,
  renderArchitectureDiagram,
} from "./systemArchitectureRenderer";
import {
  SystemArchitectureReadingKey,
  SystemArchitectureSupportingContent,
} from "./SystemArchitectureSupportingContent";
import {
  consumeSystemArchitectureViewFromLocation,
  DEFAULT_SYSTEM_ARCHITECTURE_DIAGRAM_ID,
  readSystemArchitectureViewFromLocation,
  replaceSystemArchitectureViewInLocation,
} from "./systemArchitectureViewLocation";

import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import { VNextButton, VNextCard } from "@/vnext/primitives";
import { SettingsWorkspaceChrome } from "@/vnext/routes/settings/SettingsWorkspaceChrome";

import "./systemArchitectureRoute.css";

const logger = FrontendLoggerFactory.getLogger().withComponent(
  "SystemArchitectureRoute",
);

type DiagramLoadState =
  | { diagramId: SystemArchitectureDiagramId; status: "loading" }
  | { diagramId: SystemArchitectureDiagramId; status: "ready"; svg: string }
  | { diagramId: SystemArchitectureDiagramId; status: "error" };

const formatVerifiedDate = (verifiedDate: string): string =>
  new Intl.DateTimeFormat("en-NZ", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
    year: "numeric",
  }).format(new Date(`${verifiedDate}T00:00:00Z`));

const centreMainFocus = (viewer: HTMLElement): void => {
  const focalNode = viewer.querySelector<SVGGElement>("g.node.focal");
  const contentBox = focalNode?.closest("svg")?.getBoundingClientRect();
  if (!focalNode || !contentBox) return;

  const focalBox = focalNode.getBoundingClientRect();
  viewer.scrollTo({
    behavior: "auto",
    left:
      focalBox.left -
      contentBox.left -
      (viewer.clientWidth - focalBox.width) / 2,
  });
};

const isMainFocusFullyVisible = (viewer: HTMLElement): boolean => {
  const focalNode = viewer.querySelector<SVGGElement>("g.node.focal");
  if (!focalNode) return false;

  const viewerBox = viewer.getBoundingClientRect();
  const focalBox = focalNode.getBoundingClientRect();
  return (
    focalBox.left >= viewerBox.left - 1 && focalBox.right <= viewerBox.right + 1
  );
};

export default function SystemArchitectureRoute() {
  const [selectedDiagramId, setSelectedDiagramId] =
    useState<SystemArchitectureDiagramId>(
      readSystemArchitectureViewFromLocation,
    );
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [loadState, setLoadState] = useState<DiagramLoadState>({
    diagramId: DEFAULT_SYSTEM_ARCHITECTURE_DIAGRAM_ID,
    status: "loading",
  });
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
  const requestIdRef = useRef(0);
  const viewerRef = useRef<HTMLElement>(null);
  const selectedDiagram = getSystemArchitectureDiagram(selectedDiagramId);
  const displayedLoadState: DiagramLoadState =
    loadState.diagramId === selectedDiagramId
      ? loadState
      : { diagramId: selectedDiagramId, status: "loading" };

  useEffect(() => {
    consumeSystemArchitectureViewFromLocation();
  }, []);

  const jumpToMainFocus = () => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    centreMainFocus(viewer);
    viewer.focus();
  };

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoadState({ diagramId: selectedDiagram.id, status: "loading" });

    void selectedDiagram
      .loadSource()
      .then((source) => {
        if (requestIdRef.current !== requestId) {
          return null;
        }

        return renderArchitectureDiagram(source);
      })
      .then((svg) => {
        if (svg !== null && requestIdRef.current === requestId) {
          setLoadState({ diagramId: selectedDiagram.id, status: "ready", svg });
        }
      })
      .catch(() => {
        if (requestIdRef.current !== requestId) {
          return;
        }

        logger.error(ARCHITECTURE_DIAGRAM_RENDER_ERROR_CODE, undefined, {
          diagramId: selectedDiagram.id,
          errorCode: ARCHITECTURE_DIAGRAM_RENDER_ERROR_CODE,
        });
        setLoadState({ diagramId: selectedDiagram.id, status: "error" });
      });

    return () => {
      if (requestIdRef.current === requestId) {
        requestIdRef.current += 1;
      }
    };
  }, [loadAttempt, selectedDiagram]);

  useEffect(() => {
    if (displayedLoadState.status !== "ready") {
      setHasHorizontalOverflow(false);
      return;
    }

    const viewer = viewerRef.current;
    if (!viewer) return;
    let wasOverflowing = false;
    let lastClientWidth = viewer.clientWidth;
    let userNavigatedDiagram = false;
    const markPointerNavigation = () => {
      userNavigatedDiagram = true;
    };
    const markKeyboardNavigation = (event: KeyboardEvent) => {
      if (
        [
          "ArrowLeft",
          "ArrowRight",
          "End",
          "Home",
          "PageDown",
          "PageUp",
        ].includes(event.key)
      ) {
        userNavigatedDiagram = true;
      }
    };
    const updateOverflow = () => {
      const isOverflowing = viewer.scrollWidth > viewer.clientWidth + 24;
      const widthChanged = viewer.clientWidth !== lastClientWidth;
      setHasHorizontalOverflow(isOverflowing);
      if (
        isOverflowing &&
        ((!wasOverflowing && viewer.scrollLeft === 0) ||
          (widthChanged &&
            !userNavigatedDiagram &&
            !isMainFocusFullyVisible(viewer)))
      ) {
        centreMainFocus(viewer);
      }
      wasOverflowing = isOverflowing;
      lastClientWidth = viewer.clientWidth;
    };
    updateOverflow();
    window.addEventListener("resize", updateOverflow);
    viewer.addEventListener("keydown", markKeyboardNavigation);
    viewer.addEventListener("pointerdown", markPointerNavigation);
    viewer.addEventListener("touchstart", markPointerNavigation, {
      passive: true,
    });
    viewer.addEventListener("wheel", markPointerNavigation, { passive: true });
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateOverflow);
    resizeObserver?.observe(viewer);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateOverflow);
      viewer.removeEventListener("keydown", markKeyboardNavigation);
      viewer.removeEventListener("pointerdown", markPointerNavigation);
      viewer.removeEventListener("touchstart", markPointerNavigation);
      viewer.removeEventListener("wheel", markPointerNavigation);
    };
  }, [displayedLoadState.diagramId, displayedLoadState.status]);

  return (
    <SettingsWorkspaceChrome
      subtitle="Choose a current diagram to see how HelloCashflow fits together. Sensitive details are removed, and the evidence for each view is included."
      title="System architecture"
    >
      <div className="system-architecture-route">
        <VNextCard className="system-architecture-route__surface">
          <SystemArchitectureCatalogue
            onSelect={(diagramId) => {
              if (viewerRef.current) {
                viewerRef.current.scrollLeft = 0;
              }
              replaceSystemArchitectureViewInLocation(diagramId);
              setSelectedDiagramId(diagramId);
            }}
            selectedDiagramId={selectedDiagramId}
          />

          <header className="system-architecture-route__header">
            <div className="system-architecture-route__heading">
              <span className="system-architecture-route__eyebrow">
                {selectedDiagram.level}
              </span>
              <h2>{selectedDiagram.title}</h2>
              <p>{selectedDiagram.summary}</p>
            </div>
            <dl className="system-architecture-route__metadata">
              <div>
                <dt>Scope</dt>
                <dd>{selectedDiagram.system}</dd>
              </div>
              <div>
                <dt>Owner</dt>
                <dd>{selectedDiagram.owner}</dd>
              </div>
              <div>
                <dt>Reviewed</dt>
                <dd>
                  <time dateTime={selectedDiagram.verifiedDate}>
                    {formatVerifiedDate(selectedDiagram.verifiedDate)}
                  </time>
                </dd>
              </div>
            </dl>
          </header>

          <SystemArchitectureReadingKey diagram={selectedDiagram} />

          {displayedLoadState.status === "ready" && hasHorizontalOverflow ? (
            <>
              <p className="system-architecture-route__scroll-hint">
                <span aria-hidden="true">↔</span> Scroll sideways to see the
                full diagram.
              </p>
              <div className="system-architecture-route__mobile-diagram-action">
                <VNextButton
                  aria-controls="system-architecture-diagram"
                  onClick={jumpToMainFocus}
                >
                  Jump to main focus
                </VNextButton>
              </div>
            </>
          ) : null}

          <section
            aria-label={`${selectedDiagram.title} diagram`}
            aria-describedby="system-architecture-reading-key"
            className="system-architecture-route__viewer"
            id="system-architecture-diagram"
            ref={viewerRef}
            tabIndex={0}
          >
            {displayedLoadState.status === "loading" ? (
              <p aria-live="polite" role="status">
                Loading diagram…
              </p>
            ) : null}
            {displayedLoadState.status === "error" ? (
              <div role="alert">
                <p>We could not show this diagram. Please try again.</p>
                <VNextButton
                  onClick={() => {
                    setLoadAttempt((value) => value + 1);
                    viewerRef.current?.focus();
                  }}
                >
                  Retry
                </VNextButton>
              </div>
            ) : null}
            {displayedLoadState.status === "ready" ? (
              <div
                className="system-architecture-route__svg"
                data-diagram-id={selectedDiagram.id}
                // This dedicated boundary receives only SVG sanitized by the architecture renderer.
                dangerouslySetInnerHTML={{ __html: displayedLoadState.svg }}
              />
            ) : null}
          </section>

          <SystemArchitectureSupportingContent diagram={selectedDiagram} />
        </VNextCard>
      </div>
    </SettingsWorkspaceChrome>
  );
}
